import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  loadIncomeList,
  modifyIncomeWithDocument,
  removeIncome,
  saveIncomeWithDocument,
} from '../api/income'
import removeFile from '../../Files/api/removeFile'
import {
  CreateIncomeInput,
  Income,
  IncomeFilters,
  IncomeListRequest,
  PaginatedIncomesResponse,
  PaginationMeta,
  UpdateIncomeInput,
} from '../types/income'
import { incomeMatchesFilters } from '../utils/filterIncome'

const emptyPagination: PaginationMeta = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
}

const emptyFilters: IncomeFilters = {
  name: '',
  startDate: '',
  endDate: '',
}

export const getIncomeList = createAsyncThunk(
  'incomeList/getIncomeList',
  async (request: IncomeListRequest, thunkApi) => {
    try {
      return await loadIncomeList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('getIncomeListError')
    }
  },
)

export const addIncome = createAsyncThunk(
  'incomeList/addIncome',
  async (income: CreateIncomeInput, thunkApi) => {
    try {
      return await saveIncomeWithDocument(income)
    } catch (e) {
      return thunkApi.rejectWithValue('addIncomeError')
    }
  },
)

export const updateIncome = createAsyncThunk(
  'incomeList/updateIncome',
  async (income: UpdateIncomeInput, thunkApi) => {
    try {
      return await modifyIncomeWithDocument(income)
    } catch (e) {
      return thunkApi.rejectWithValue('updateIncomeError')
    }
  },
)

export const deleteIncome = createAsyncThunk(
  'incomeList/deleteIncome',
  async (id: string, thunkApi) => {
    try {
      const state = thunkApi.getState() as {
        incomeList?: { incomes?: Income[] }
      }
      const existing = state.incomeList?.incomes?.find(
        (income) => income.id === id,
      )
      const result = await removeIncome(id)
      const fileIds = Array.from(
        new Set(
          [
            ...(existing?.documentFiles || []).map((file) => file.id),
            existing?.documentFileId || '',
          ].filter(Boolean),
        ),
      )

      await Promise.all(
        fileIds.map((fileId) => removeFile(fileId).catch(() => undefined)),
      )

      return result
    } catch (e) {
      return thunkApi.rejectWithValue('deleteIncomeError')
    }
  },
)

interface IncomeSliceState {
  incomes: Income[]
  loading: boolean
  error?: SerializedError | string
  filters: IncomeFilters
  pagination: PaginationMeta
  activeListRequestId?: string
}

const initialState: IncomeSliceState = {
  incomes: [],
  loading: false,
  error: undefined,
  filters: emptyFilters,
  pagination: emptyPagination,
  activeListRequestId: undefined,
}

const incomeListSlice = createSlice({
  name: 'incomeList',
  initialState,
  reducers: {
    setIncomeFilters: (
      state: IncomeSliceState,
      action: PayloadAction<IncomeFilters>,
    ) => {
      state.filters = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getIncomeList.fulfilled,
      (
        state,
        action: PayloadAction<PaginatedIncomesResponse> & { meta: any },
      ) => {
        if (state.activeListRequestId !== action.meta.requestId) {
          return
        }

        state.incomes = action.payload.items
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        }
        state.activeListRequestId = undefined
        state.loading = false
        state.error = undefined
      },
    )
    builder.addCase(getIncomeList.pending, (state, action: any) => {
      state.activeListRequestId = action.meta.requestId
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getIncomeList.rejected, (state, action: any) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.activeListRequestId = undefined
      state.loading = false
      state.error = action.error
    })
    builder.addCase(
      addIncome.fulfilled,
      (state, action: PayloadAction<Income>) => {
        if (incomeMatchesFilters(action.payload, state.filters)) {
          if (state.pagination.page === 1) {
            state.incomes = [action.payload, ...state.incomes].slice(
              0,
              state.pagination.limit || 25,
            )
          }
          state.pagination.total += 1
          state.pagination.totalPages = Math.ceil(
            state.pagination.total / (state.pagination.limit || 25),
          )
        }

        state.loading = false
        state.error = undefined
      },
    )
    builder.addCase(addIncome.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addIncome.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(
      updateIncome.fulfilled,
      (state, action: PayloadAction<Income>) => {
        state.incomes = state.incomes.map((income) =>
          income.id === action.payload.id ? action.payload : income,
        )
        state.loading = false
        state.error = undefined
      },
    )
    builder.addCase(updateIncome.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateIncome.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(
      deleteIncome.fulfilled,
      (state, action: PayloadAction<string>) => {
        const wasOnCurrentPage = state.incomes.some(
          (income) => income.id === action.payload,
        )

        state.incomes = state.incomes.filter(
          (income) => income.id !== action.payload,
        )

        if (wasOnCurrentPage && state.pagination.total > 0) {
          state.pagination.total -= 1
          state.pagination.totalPages =
            state.pagination.total === 0
              ? 0
              : Math.ceil(state.pagination.total / state.pagination.limit)
        }

        state.loading = false
        state.error = undefined
      },
    )
    builder.addCase(deleteIncome.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteIncome.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
  },
})

export const { setIncomeFilters } = incomeListSlice.actions
export default incomeListSlice.reducer
